import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@repo/common/utils/cn";
import { _disabledPointer, _focus } from "./shared-styles";
import { Icon, IconType } from "./icon";

const _base =
  "inline-flex items-center justify-center whitespace-nowrap rounded gap-1 font-semibold text-sm leading-4 transition-colors tabular-nums lining-nums";

// Fill variants
const _fillDefault =
  "border-none bg-action-primary text-action-primary-inverse hover:bg-action-primary-hover active:bg-action-primary-hover";
const _fillDanger =
  "border-none bg-action-destructive text-action-destructive-inverse hover:bg-action-destructive-hover active:bg-action-destructive-hover";

// Outline variants
const _outlineDefault =
  "bg-background text-foreground border border-solid border-border hover:bg-action-neutral-inverse-hover active:bg-action-neutral-inverse-hover";

// Ghost variants
const _ghostDefault =
  "bg-transparent text-foreground hover:bg-action-neutral-inverse-hover active:bg-action-neutral-inverse-hover";
const _ghostDanger =
  "bg-transparent text-action-destructive hover:bg-action-destructive-inverse-hover hover:text-action-destructive-hover active:bg-action-destructive-inverse-hover active:text-action-destructive-hover ";

const _dangerFocus = "focus-visible:ring-action-destructive-hover";

// Link variants - completely override base styles
// max-md classes re-assert the reset over the size variants' mobile sizing
// (tailwind-merge only lets same-modifier classes override each other).
const _linkBase =
  "relative inline-flex items-center gap-1 p-0 h-4 max-md:p-0 max-md:h-4";
const _linkTypography =
  "font-normal text-sm leading-4 text-action-neutral whitespace-nowrap";
const _linkInteractive = "transition-colors cursor-pointer";
const _linkHover =
  "before:absolute before:rounded-md before:bg-action-neutral-inverse-hover before:opacity-0 before:transition-opacity before:-z-10 hover:before:opacity-100";
const _removeFocus =
  "focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-transparent";
const _linkFocus =
  "after:absolute after:rounded-md focus-visible:after:ring-2 focus-visible:after:ring-offset-2 focus-visible:after:ring-offset-background focus-visible:after:ring-action-primary-hover";

const _linkStyles = [
  _linkBase,
  _linkTypography,
  _linkInteractive,
  _linkHover,
  _removeFocus,
  _linkFocus,
  _disabledPointer,
];

const _linkSm = "before:inset-[-4px] after:inset-[-4px]";
const _linkMd = "before:inset-[-6px] after:inset-[-6px]";

const buttonVariants = cva([_base, _focus, _disabledPointer], {
  variants: {
    variant: {
      fill: "",
      outline: _outlineDefault,
      ghost: "",
      link: "",
    },
    intent: {
      default: "",
      danger: _dangerFocus,
    },
    size: {
      // Phone viewports (`max-md`, <768px) get taller touch targets — sm
      // 28→32px, md 32→40px — via vertical padding; horizontal padding is
      // the desktop value at every width.
      md: "h-8 px-4 py-2 max-md:h-10 max-md:py-3",
      // rounded-sm (6px) overrides the base `rounded` (12px) for the small size.
      sm: "h-7 rounded-sm px-3 py-1.5 max-md:h-8 max-md:py-2",
    },
  },
  compoundVariants: [
    // Fill variants
    {
      variant: "fill",
      intent: "default",
      className: _fillDefault,
    },
    {
      variant: "fill",
      intent: "danger",
      className: _fillDanger,
    },
    // Ghost variants
    {
      variant: "ghost",
      intent: "default",
      className: _ghostDefault,
    },
    {
      variant: "ghost",
      intent: "danger",
      className: _ghostDanger,
    },
    // Link variants
    {
      variant: "link",
      size: "md",
      className: [_linkStyles, _linkMd],
    },
    {
      variant: "link",
      size: "sm",
      className: [_linkStyles, _linkSm],
    },
  ],
  defaultVariants: {
    variant: "fill",
    intent: "default",
    size: "md",
  },
});

export type ButtonProps = VariantProps<typeof buttonVariants> & {
  /**
   * Render the single child element in place of `<button>`, merging Button
   * props onto it. The child must be exactly one React element that forwards
   * `ref` and `className` — not a fragment, string, or multiple children.
   * Icons render inside the child, around its own content.
   */
  asChild?: boolean;
  iconLeading?: IconType;
  iconTrailing?: IconType;
};

// radix slot uses cloneElement which requires forwardRef to work correctly
// when using asChild, the component is not a button, so we need to use the ref prop
const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & ButtonProps
>(
  (
    {
      className,
      variant,
      size,
      intent,
      asChild = false,
      iconLeading,
      iconTrailing,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? (Slot as React.ElementType) : "button";

    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, size, intent, className }))}
        ref={ref}
        {...props}
      >
        {iconLeading && <Icon icon={iconLeading} size={"sm"} />}
        <Slottable>{children}</Slottable>
        {iconTrailing && <Icon icon={iconTrailing} size={"sm"} />}
      </Comp>
    );
  },
);
Button.displayName = "Button";

const buttonStyles = {
  base: _base,
  fillDefault: _fillDefault,
  fillDanger: _fillDanger,
  outlineDefault: _outlineDefault,
  ghostDefault: _ghostDefault,
  ghostDanger: _ghostDanger,
  dangerFocus: _dangerFocus,
  linkStyles: _linkStyles,
  linkMd: _linkMd,
  linkSm: _linkSm,
};

export { Button, buttonVariants, buttonStyles };
